<template>
  <div class="custom-multi-select-container" :class="{ 'is-open': isOpen }">

    <!-- Trigger Button -->
    <div 
      class="custom-multi-select-trigger"
      @click="toggleDropdown"
      :class="{ 'is-open': isOpen, 'is-error': isError }"
      :tabindex="disabled ? -1 : 0"
      @keydown.enter="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
      @keydown.escape="closeDropdown"
    >
      <div class="trigger-content">
        <i v-if="prefixIcon" :class="['mr-2', prefixIcon]"></i>
        <span class="trigger-text">{{ displayText }}</span>
        <div class="w-2"></div>
        <i class="fa fa-chevron-down transition-transform duration-200" :class="{ 'rotate-180': isOpen }"></i>
      </div>
    </div>

    <!-- Dropdown -->
    <Transition name="dropdown">
      <div v-if="isOpen" class="custom-multi-select-dropdown">
      
        <div class="dropdown-header">
          <span class="header-text">{{ headerText }}</span>
        <!-- Header     <div class="header-actions">
            <button 
              v-if="showSelectAll"
              @click="selectAll"
              class="select-all-btn"
            >
              {{ $t('select_all') || 'Select All' }}
            </button>
            <button 
              v-if="showClearAll"
              @click="clearAll"
              class="clear-all-btn"
            >
              {{ $t('clear_all') || 'Clear All' }}
            </button>
          </div>-->
        </div>

        <!-- Search -->
        <div v-if="searchable" class="dropdown-search">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="searchPlaceholder"
            class="search-input"
            @click.stop
          />
        </div>

        <!-- Options List -->
        <div class="dropdown-content" @click.stop>
          <div 
            v-for="option in filteredOptions" 
            :key="option.value"
            class="dropdown-option"
            :class="{ 'is-selected': isSelected(option) }"
            @click.stop="toggleOption(option)"
          >
            <div class="checkbox-container">
              <div class="custom-checkbox" :class="{ 'is-checked': isSelected(option) }">
                <i v-if="isSelected(option)" class="fa fa-check checkbox-icon"></i>
              </div>
            </div>
            <span class="option-text">{{ option.text }}</span>
          </div>
          
          <!-- No results -->
          <div v-if="filteredOptions.length === 0" class="no-results">
            <i class="fa fa-search mr-2"></i>
            {{ noResultsText }}
          </div>
        </div>
      </div>
    </Transition>

    <!-- Error message -->
    <div v-if="isError && errorMsg" class="error-message mt-2 flex items-center text-red-500 text-sm">
      <i class="fa fa-exclamation-circle mr-2"></i>
      {{ errorMsg }}
    </div>
    
    <!-- Helper text -->
    <div v-if="helperText && !isError" class="helper-text mt-2 text-gray-500 dark:text-gray-400 text-sm">
      {{ helperText }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomMultiSelect',
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Select Columns'
    },
    headerText: {
      type: String,
      default: 'Which columns to display'
    },
    trackBy: {
      type: String,
      default: 'value'
    },
    label: {
      type: String,
      default: ''
    },
    labelKey: {
      type: String,
      default: 'text'
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isError: {
      type: Boolean,
      default: false
    },
    errorMsg: {
      type: String,
      default: ''
    },
    helperText: {
      type: String,
      default: ''
    },
    searchable: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...'
    },
    noResultsText: {
      type: String,
      default: 'No results found'
    },
    showSelectAll: {
      type: Boolean,
      default: false
    },
    showClearAll: {
      type: Boolean,
      default: false
    },
    prefixIcon: {
      type: String,
      default: 'fa fa-columns'
    },
    maxDisplayItems: {
      type: Number,
      default: 3
    }
  },
  emits: ['update:modelValue', 'focus', 'blur'],
  data() {
    return {
      isOpen: false,
      isToggling: false,
      searchQuery: ''
    }
  },
  computed: {
    displayText() {
      if (this.modelValue.length === 0) {
        return this.placeholder
      }
      
      if (this.modelValue.length <= this.maxDisplayItems) {
        return this.modelValue.map(item => item[this.labelKey]).join(', ')
      }
      
      return `${this.modelValue.length} `+this.$t('columns_selected')
    },
    filteredOptions() {
      if (!this.searchable || !this.searchQuery) {
        return this.options
      }
      
      const query = this.searchQuery.toLowerCase()
      return this.options.filter(option => 
        option[this.labelKey].toLowerCase().includes(query)
      )
    }
  },
  methods: {
    toggleDropdown() {
      if (this.disabled) return
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.$emit('focus')
      } else {
        this.$emit('blur')
      }
    },
    closeDropdown() {
      this.isOpen = false
      this.$emit('blur')
    },
    toggleOption(option) {
      if (this.disabled || this.isToggling) return
      this.isToggling = true
      
      const currentValue = [...this.modelValue]
      const optionValue = option[this.trackBy]
      const index = currentValue.findIndex(item => item[this.trackBy] === optionValue)
      
      if (index > -1) {
        // Remove if already selected
        currentValue.splice(index, 1)
      } else {
        // Add if not selected
        currentValue.push(option)
      }
      
      this.$emit('update:modelValue', currentValue)
      
      // Reset toggle flag after a short delay
      setTimeout(() => {
        this.isToggling = false
      }, 100)
    },
    isSelected(option) {
      const optionValue = option[this.trackBy]
      return this.modelValue.some(item => item[this.trackBy] === optionValue)
    },
    selectAll() {
      this.$emit('update:modelValue', [...this.filteredOptions])
    },
    clearAll() {
      this.$emit('update:modelValue', [])
    }
  },
  mounted() {
    // Close dropdown when clicking outside
    this.handleClickOutside = (e) => {
      if (this.isOpen && !this.$el.contains(e.target)) {
        this.closeDropdown()
      }
    }
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    // Clean up event listener
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside)
    }
  }
}
</script>

<style scoped>
.custom-multi-select-container {
  position: relative;
  min-width: 200px;
  max-width: 100%;
}

.custom-multi-select-trigger {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  min-height: 42px;
  display: flex;
  align-items: center;
  font-size: 14px;
}

.custom-multi-select-trigger:hover:not(.is-error) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.custom-multi-select-trigger.is-open {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.custom-multi-select-trigger.is-error {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.custom-multi-select-trigger:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.trigger-content {
  display: flex;
  align-items: center;
  width: 100%;
  color: #374151;
  font-weight: 500;
}

.trigger-text {
  flex: 1;
  font-size: 14px;
  text-align: left;
}

.custom-multi-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
  margin-top: 4px;
}

.dropdown-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-text {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.select-all-btn,
.clear-all-btn {
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.select-all-btn {
  background: #3b82f6;
  color: white;
}

.select-all-btn:hover {
  background: #2563eb;
}

.clear-all-btn {
  background: #f3f4f6;
  color: #374151;
}

.clear-all-btn:hover {
  background: #e5e7eb;
}

.dropdown-search {
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s ease;
}

.search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.dropdown-content {
  max-height: 200px;
  overflow-y: auto;
  padding: 4px 0;
}

.dropdown-option {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  user-select: none;
}

.dropdown-option:hover {
  background-color: #f3f4f6;
}

.dropdown-option.is-selected {
  background-color: #eff6ff;
}

.checkbox-container {
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-checkbox {
  width: 18px;
  height: 18px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  transform: scale(1);
}

.custom-checkbox.is-checked {
  background: #3b82f6;
  border-color: #3b82f6;
  transform: scale(1.05);
  animation: checkPulse 0.3s ease-out;
}

@keyframes checkPulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1.05);
  }
}

.checkbox-icon {
  color: white;
  font-size: 10px;
  font-weight: bold;
}

.option-text {
  flex: 1;
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

.no-results {
  padding: 16px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Error message animation */
.error-message {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Helper text styling */
.helper-text {
  transition: all 0.2s ease-in-out;
}

/* Dropdown transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease-out;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}


/* Dark mode support */
.dark .custom-multi-select-trigger {
  background: #1f2937;
  border-color: #4b5563;
  color: white;
}

.dark .custom-multi-select-trigger:hover:not(.is-error) {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
}

.dark .custom-multi-select-trigger.is-open {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
}

.dark .custom-multi-select-trigger.is-error {
  border-color: #f87171;
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.1);
}

.dark .trigger-content {
  color: white;
}

.dark .custom-multi-select-dropdown {
  background: #1f2937;
  border-color: #4b5563;
}

.dark .header-text {
  color: #d1d5db;
}

.dark .dropdown-header {
  border-bottom-color: #4b5563;
}

.dark .select-all-btn {
  background: #3b82f6;
  color: white;
}

.dark .clear-all-btn {
  background: #374151;
  color: #d1d5db;
}

.dark .clear-all-btn:hover {
  background: #4b5563;
}

.dark .dropdown-search {
  border-bottom-color: #4b5563;
}

.dark .search-input {
  background: #374151;
  border-color: #4b5563;
  color: white;
}

.dark .search-input:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
}

.dark .dropdown-option:hover {
  background-color: #374151;
}

.dark .dropdown-option.is-selected {
  background-color: #1e3a8a;
}

.dark .custom-checkbox {
  background: #374151;
  border-color: #6b7280;
}

.dark .custom-checkbox.is-checked {
  background: #3b82f6;
  border-color: #3b82f6;
}

.dark .option-text {
  color: #d1d5db;
}

.dark .no-results {
  color: #9ca3af;
}

/* RTL Support */
[dir="rtl"] .checkbox-container {
  margin-right: 0;
  margin-left: 12px;
}

[dir="rtl"] .dropdown-header {
  text-align: center;
}

[dir="rtl"] .dropdown-option {
  flex-direction: row-reverse;
}

[dir="rtl"] .trigger-text {
  text-align: right;
}

/* Custom scrollbar */
.dropdown-content::-webkit-scrollbar {
  width: 6px;
}

.dropdown-content::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.dropdown-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.dropdown-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.dark .dropdown-content::-webkit-scrollbar-track {
  background: #374151;
}

.dark .dropdown-content::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark .dropdown-content::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Responsive design */
@media (max-width: 640px) {
  .custom-multi-select-container {
    min-width: 200px;
    max-width: 100%;
  }
  
  .custom-multi-select-trigger {
    padding: 8px 10px;
    min-height: 40px;
    font-size: 13px;
  }
  
  .dropdown-option {
    padding: 6px 12px;
  }
  
  .header-text {
    font-size: 13px;
  }
  
  .dropdown-header {
    padding: 8px 12px;
  }
  
  .header-actions {
    flex-direction: column;
    gap: 4px;
  }
  
  .select-all-btn,
  .clear-all-btn {
    font-size: 11px;
    padding: 2px 6px;
  }
}

/* Disabled state */
.custom-multi-select-trigger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f9fafb;
}

.dark .custom-multi-select-trigger:disabled {
  background: #374151;
  color: #6b7280;
}

/* Focus states for accessibility */
.custom-multi-select-trigger:focus {
  outline: none;
}

/* Animation for smooth transitions */
.custom-multi-select-dropdown {
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
